import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import { Button, Field, Input, Select, Textarea, Card } from './UI';

/**
 * The questions some events ask as an attendee scans in.
 *
 * Two of them matter to the server: the one that identifies the person (their
 * employee id, mobile or email, whichever the organiser chose) and the one
 * that counts who came along with them. The rest are the organiser's own.
 *
 * `form` is the shape GET /api/forms/attendance returns, and what the scan
 * endpoint hands back when it asks for the form before marking anybody in.
 */
export default function CheckInForm({
  form, busy, onSubmit, onCancel, prefill,
  // Set once the server has said this detail is not on the event's list and
  // the organiser allows people to add themselves.
  addSelf = false, companies = [], company, onCompany, title, note,
}) {
  const initial = useMemo(() => {
    const out = { ...(prefill || {}) };
    if (form?.companionsKey && out[form.companionsKey] === undefined) out[form.companionsKey] = '';
    return out;
  }, [form, prefill]);

  const [answers, setAnswers] = useState(initial);
  const set = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  // A walk-in needs their name; otherwise the name question is the organiser's
  // business and stays out of the way of people already on the list.
  const fields = [...(form?.fields || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((f) => addSelf || f.key !== form?.nameKey);
  const expected = form?.expectedCompanions;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Card padding={20}>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>{title || form?.title || 'Before you go in'}</h2>
        <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 6, lineHeight: 1.6 }}>
          {note || form?.description
            || `Confirm your ${form?.identifierLabel || 'details'} and say how many people are with you — then you're marked present.`}
        </p>

        {addSelf && companies.length > 1 && (
          <Field label="Which company are you with?" required>
            <Select value={company || ''} onChange={(e) => onCompany?.(e.target.value)}>
              <option value="">Choose your company…</option>
              {companies.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </Select>
          </Field>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginTop: 16 }}>
          {fields.map((f) => {
            const isCompanions = f.key === form?.companionsKey;
            const value = answers[f.key] ?? '';
            const hint = isCompanions
              ? (Number.isFinite(expected)
                ? `The list says ${expected} ${expected === 1 ? 'person' : 'people'} with you. Put the real number — it is what we count.`
                : 'Not counting yourself. Leave it at 0 if you came alone.')
              : f.hint;
            return (
              <Field
                key={f.key}
                label={f.label}
                hint={hint}
                required={f.required || (isCompanions && form?.requireCompanions)}
              >
                {f.type === 'textarea' ? (
                  <Textarea value={value} placeholder={f.placeholder} onChange={(e) => set(f.key, e.target.value)} />
                ) : f.type === 'select' || f.type === 'radio' ? (
                  <Select value={value} onChange={(e) => set(f.key, e.target.value)}>
                    <option value="">Choose…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </Select>
                ) : f.type === 'checkbox' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {(f.options || []).map((o) => {
                      const picked = Array.isArray(value) ? value : [];
                      return (
                        <label key={o} style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 13.5 }}>
                          <input
                            type="checkbox" checked={picked.includes(o)} style={{ accentColor: 'var(--gold-400)' }}
                            onChange={(e) => set(f.key, e.target.checked ? [...picked, o] : picked.filter((x) => x !== o))}
                          />
                          {o}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <Input
                    type={isCompanions ? 'number' : f.type === 'tel' ? 'tel' : f.type === 'email' ? 'email' : f.type}
                    inputMode={isCompanions ? 'numeric' : undefined}
                    min={isCompanions ? 0 : undefined}
                    max={isCompanions && form?.maxCompanions ? form.maxCompanions : undefined}
                    value={value}
                    placeholder={f.placeholder}
                    onChange={(e) => set(f.key, e.target.value)}
                  />
                )}
              </Field>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          {onCancel && <Button variant="subtle" onClick={onCancel} disabled={busy}>Cancel</Button>}
          <Button icon={Users} loading={busy} onClick={() => onSubmit(answers)} style={{ flex: 1 }}>
            {addSelf ? 'Add me and check in' : 'Check me in'}
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}
